import React from "react";
import { MailCheck } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import loginLogo from "../../../../assets/page/login_logo.svg";
import loginCircleLeft from "../../../../assets/page/login_circle_left.svg";
import loginCircleRight from "../../../../assets/page/login_circle_right.svg";
import { TERMS_URL } from "../../../../config/runtime";

export const PasswordLinkSentPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const email = location.state?.email || "";

  React.useEffect(() => {
    if (!email) {
      navigate("/forgot-password");
    }
  }, [email, navigate]);

  if (!email) {
    return null;
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-white text-[#0f172a]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-x-0 top-0 h-[64vh] bg-gradient-to-b from-[#f8fbfa] via-white to-white" />
        <img
          src={loginCircleLeft}
          alt=""
          aria-hidden="true"
          className="absolute left-0 bottom-16 w-[220px] opacity-35"
        />
        <img
          src={loginCircleRight}
          alt=""
          aria-hidden="true"
          className="absolute right-0 bottom-16 w-[220px] opacity-35"
        />
      </div>

      <header className="relative z-10 flex items-center justify-between px-6 py-5 md:px-8 md:py-7">
        <Link to="/login" className="shrink-0">
          <img
            src={loginLogo}
            alt="CivicPoints"
            className="h-7 w-auto md:h-9"
          />
        </Link>
        <Link
          to="/login"
          className="rounded-full px-2 py-1 text-[15px] font-semibold text-[#0b5c43] transition-colors hover:text-[#00a57f]"
        >
          {t("auth.login")}
        </Link>
      </header>

      <main className="relative z-10 flex min-h-[calc(100vh-88px)] flex-col items-center justify-center px-5 pb-10 pt-8 md:px-8">
        <section className="w-full max-w-[600px] rounded-[26px] border border-[#e7ebf1] bg-white px-6 py-10 shadow-[0_24px_70px_rgba(15,23,42,0.10)] md:px-12 md:py-14">
          <div className="mx-auto mb-7 flex w-full max-w-[100px] items-center justify-center">
            <div className="relative flex h-[118px] w-[118px] items-center justify-center rounded-full bg-[#eefaf7]">
              <MailCheck
                className="h-14 w-14 text-[#0b5c43]"
                strokeWidth={1.8}
              />
            </div>
          </div>

          <div className="text-center">
            <h1 className="[font-family:'Cal_Sans-Regular',Helvetica] text-[30px] font-bold leading-tight text-[#0b5c43] md:text-[38px]">
              {t("auth.reset_link_sent_title")}
            </h1>
            <p className="mx-auto mt-4 max-w-[460px] text-[16px] leading-7 text-[#6f7686] md:text-[17px]">
              <Trans
                i18nKey="auth.reset_link_sent_description"
                values={{ email }}
                components={{
                  b: <b className="font-semibold text-[#0b5c43]" />,
                }}
              />
            </p>
            <p className="mx-auto mt-4 max-w-[460px] text-[16px] leading-7 text-[#6f7686] md:text-[17px]">
              {t("auth.reset_link_sent_help")}{" "}
              <span className="font-semibold text-[#0b5c43] underline">
                support@civicpoints.io
              </span>
              .
            </p>
          </div>
        </section>

        <footer className="mt-14 text-center text-[14px] text-[#7a8192] md:mt-16">
          <a
            type="button"
            href={TERMS_URL}
            className="transition-colors hover:text-[#0b5c43]"
          >
            {t("auth.footer_terms")}
          </a>
          <span className="mx-3 text-[#c5cad4]">|</span>
          <a
            type="button"
            href="https://civicpoints.io/privacy-policy"
            className="transition-colors hover:text-[#0b5c43]"
          >
            {t("auth.footer_privacy")}
          </a>
          <span className="mx-3 text-[#c5cad4]">|</span>
          <a
            type="button"
            href="https://helpdesk.civicpoints.io/en/customer/create-ticket/"
            className="transition-colors hover:text-[#0b5c43]"
          >
            {t("auth.footer_help")}
          </a>
        </footer>
      </main>
    </div>
  );
};

export default PasswordLinkSentPage;
